import { from as observableFrom, BehaviorSubject, Observable, throwError as observableThrowError, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { LocalStorageService } from 'angular-2-local-storage';
import { LocationService } from './location.service';
import { QueryParameter } from '../interfaces/queryString';
import { FilterSet } from './filter-set.service';

import { share } from 'rxjs/operators';



export interface SapiQueryResponse {
	totalAmount: number;
	items: SapiEntity[];
	facets?: any;
	query?: string;
}

export interface SapiFacetResponse {
	numfound: number;
	facets?: { [property: string]: (string | number)[] } | null;
	facet_queries?: { [property: string]: number };
	facetList?: string[];
}

export interface SapiEntity {
	id?: string;
	type?: string;
	status?: string;
	parentEntityType?: string;
	parentEntityId?: string;
	originalfilename?: string;
	uploadIdentifier?: string;
	createddate?: string;
	updateddate?: string;
	publisheddate?: string;
	createdBy?: string;
	updatedBy?: string;
	body?: any;
	title?: string;
	name?: string;
	code?: string;
	items?: any[];
	relationLabel?: string[];
}

export interface SapiEntityVersion {
	id?: string;
	size?: number;
	isLatest?: boolean;
	date?: string;
	metadata?: any;
	versionState?: string;
}

export interface SapiErrorMessage {
	userFriendlyLabel: string;
	time: string;
	message: string;
}

export interface SapiViolation {
	code: string;
	text: string;
	user?: string;
	field?: string;
	conflictingObject: SapiEntity;
}

export interface SapiError {
	type: string;
	code: string;
	messages?: SapiErrorMessage[];
	error: boolean;
	violations?: SapiViolation[];
	body?: string;
}

export interface SapiChange {
	request: 'create' | 'get' | 'update' | 'delete' | 'query' | 'websocket';
	entityName: string;
	data?: any;
	response?: any;
}
@Injectable()
export class SapiService {

	sapiServiceSubject: BehaviorSubject<SapiChange>;

	constructor(
		private locationService: LocationService,
		private localStorageService: LocalStorageService,
		private http: HttpClient) {
		// {request: null, entityName: null, data: { id: null }}
		this.sapiServiceSubject = new BehaviorSubject(null);
	}

	getSapiChanges(): Observable<SapiChange> {
		return this.sapiServiceSubject.asObservable();
	}

	getSessionToken(): string {
		const sessToken = this.localStorageService.get('ovp-sessionToken');
		return ((sessToken && sessToken.toString().length > 0) ? sessToken.toString() : 'set');
	}

	determineRequestOptions(extraHeaders?: any) {
		const sessToken = this.getSessionToken();
		let headers = new HttpHeaders();
		headers = headers.append('sesstoken', sessToken);
		let params = new HttpParams();
		let responseType;

		if (extraHeaders && Object.keys(extraHeaders).length) {
			Object.keys(extraHeaders).forEach((key) => {
				if (key === 'params') {
					const extraParams = extraHeaders[key];
					if (extraParams && Object.keys(extraParams).length) {
						Object.keys(extraParams).forEach((param) => {
							params = params.set(param, extraParams[param]);
						});
					}
				} else if (key === 'responseType') {
					responseType = extraHeaders[key];
				} else {
					headers = headers.append(key, extraHeaders[key]);
				}
			});
		}
		const options = { headers, withCredentials: true, params };
		if (responseType) {
			const optionKey = 'responseType';
			options[optionKey] = responseType;
		}
		return options;
	}

	postUrl(url: string, params?: HttpParams | any): Observable<any> {
		const requestOptions = this.determineRequestOptions();

		return this.http.post(this.locationService.server + '/sapi' + url, params, requestOptions);
	}

	putUrl(url: string, params?: HttpParams | any): Observable<any> {
		const requestOptions = this.determineRequestOptions();

		return this.http.put(this.locationService.server + '/sapi' + url, params, requestOptions);
	}

	getUrlFromBase(url: string, params: HttpParams | any = null, extraRequestOptions?): Observable<any> {
		const requestOptions = this.determineRequestOptions(extraRequestOptions);
		if (params) {
			requestOptions.params = params;
		}

		return this.http.get(this.locationService.server + url, requestOptions);
	}

	getUrl(url: string, params: HttpParams | any = null, extraRequestOptions?): Observable<any> {
		return this.getUrlFromBase('/sapi' + url, params, extraRequestOptions);
	}

	getRandom() {
		return this.http.get(this.locationService.server + '/sapi/getRandom', { withCredentials: true });
	}

	download(entityName: string, id: number | string, extra: string = '', fileName?: string): Observable<any> {
		let url = this.locationService.server + '/sapi/' + entityName;
		url += (id ? '/' + id : '');
		url += (extra ? '/' + extra : '');

		return this.downloadFromUrl(url, fileName || (entityName + '_' + id + (extra ? '_' + extra : '')));
	}

	downloadFromUrl(url: string, filename: string, blobSizeMinimum: number = 0, extension: string = null): Observable<any> {
		const xhr = new XMLHttpRequest();

		const sessToken = this.getSessionToken();

		return observableFrom(
			new Promise((resolve, reject) => {
				xhr.open('GET', url);
				if (sessToken) {
					xhr.setRequestHeader('sessToken', sessToken);
				}
				if (url.indexOf('bbvms.com') > -1) {
					xhr.withCredentials = true;
				} else {
					xhr.withCredentials = false;
				}

				xhr.responseType = 'blob';

				xhr.onload = () => {
					const blob = new Blob([xhr.response]);
					if (blob.size >= blobSizeMinimum && xhr.status === 200) {
						const downloadUrl = window.URL.createObjectURL(blob);

						const link = document.createElement('a');
						link.href = downloadUrl;
						link.download = filename;
						if (xhr.response.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
							link.download += '.xlsx';
						} else if (xhr.response.type === 'text/csv') {
							link.download += '.csv';
						} else if (xhr.response.type === 'text/srt') {
							link.download += '.srt';
						} else if (xhr.response.type === 'application/xml') {
							link.download += '.xml';
						} else if (extension) {
							link.download += extension;
						}
						document.body.appendChild(link);
						link.click();

						setTimeout(() => {
							window.URL.revokeObjectURL(downloadUrl);
							document.body.removeChild(link);
						}, 100);
						resolve(true);
					} else {
						let json: any;
						if (blob.size < blobSizeMinimum || blob.size === 0) {
							json = 'FILE_EMPTY';
						} else {
							const reader = new FileReader();
							reader.onload = () => {
								json = JSON.parse(reader.result.toString());
							};
							reader.readAsText(blob);
						}
						reject(json);
					}
				};
				xhr.send();
			}).catch((error) => {
				throw error;
			})
		);
	}

	get(entityName: string, id: number | string, extra: string = '', extraHeaders?: any): Observable<SapiEntity> {

		const url = this.locationService.server + '/sapi/' + entityName + '/' + id + (extra ? '/' + extra : '');

		return this.http.get(url, this.determineRequestOptions(extraHeaders))
			.pipe(
				map(
					(res: any) => {
						this.sapiServiceSubject.next({ request: 'get', entityName, data: [res] });
						return res as SapiEntity;
					}
				),
				share(),
				catchError((err: any) => observableThrowError(err.error as SapiError))
			);
	}

	getMulti(entityName: string, ids: string[], extra: string = '', extraHeaders?: any): Observable<SapiEntity[]> {
		const requests: Observable<any>[] = [];
		ids.forEach((id: string) => {
			const url = this.locationService.server + '/sapi/' + entityName + '/' + id + (extra ? '/' + extra : '');
			requests.push(this.http.get(url, this.determineRequestOptions(extraHeaders)));
		});

		return forkJoin(requests)
			.pipe(
				map(
					(res: any[]) => {
						if (res && res.length) {
							res.forEach((result) => {
								this.sapiServiceSubject.next({ request: 'get', entityName, data: [result] });
							});
						}
						return res as SapiEntity[];
					}
				),
				share(),
				catchError((err: any) => observableThrowError(err.error as SapiError))
			);
	}

	update(entityName: string, entity: SapiEntity, force: boolean = false, extraHeaders?: any): Observable<SapiEntity> {

		const url = this.locationService.server + '/sapi/' + entityName + '/' + entity.id + (force ? '' : '?softsave=true');

		return this.http.put(url, JSON.stringify(entity), this.determineRequestOptions(extraHeaders))
			.pipe(
				map((response: any) => {

					this.sapiServiceSubject.next({ request: 'update', entityName, data: [entity] });

					return response as SapiEntity;
				}),
				share(),
				catchError((err: any) => observableThrowError(err.error as SapiError))
			);
	}

	updateMultiple(entityName: string, entities: any[], property: string, value: any, force: boolean = false) {

		// This method only works with mediaclips as of now
		if (entityName !== 'mediaclip') {
			return;
		}

		const url = this.locationService.server + '/sapi/' + entityName + '/batch';
		const args = [{ property, items: entities, action: 'set', value }];

		return this.http.post(url, JSON.stringify(args), this.determineRequestOptions())
			.pipe(
				map((response: any) => {

					this.sapiServiceSubject.next({ request: 'update', entityName, data: entities, response });
					return response as SapiEntity;
				}),
				share(),
				catchError((err: any) => observableThrowError(err.error as SapiError))
			);
	}

	create(entityName: string, entity: SapiEntity, force: boolean = false, extraHeaders?: any): Observable<SapiEntity> {

		const url = this.locationService.server + '/sapi/' + entityName + (force ? '' : '?softsave=true');

		return this.http.post(url, JSON.stringify(entity), this.determineRequestOptions(extraHeaders))
			.pipe(
				map((response: any) => {

					this.sapiServiceSubject.next({ request: 'create', entityName, data: entity, response });

					return response as SapiEntity;
				}),
				share(),
				catchError((err: any) => observableThrowError(err.error as SapiError))
			);
	}

	deleteField(urlPart: string): Observable<any> {

		const url = this.locationService.server + '/sapi/' + urlPart;

		return this.http.delete(url, this.determineRequestOptions())
			.pipe(
				share(),
				catchError((err: any) => observableThrowError(err.error as SapiError))
			);
	}

	delete(entityName: string, id: string, extra: string = ''): Observable<any> {

		const url = this.locationService.server + '/sapi/' + entityName + '/' + id + (extra ? '/' + extra : '');

		this.sapiServiceSubject.next({ request: 'delete', entityName, data: [id] });

		return this.http.delete(url, this.determineRequestOptions())
			.pipe(
				share(),
				catchError((err: any) => observableThrowError(err.error as SapiError))
			);
	}

	deleteMultiple(entityName: string, ids: string[]): Observable<any> {

		const url = this.locationService.server + '/sapi/' + entityName + '/batch';
		const args = JSON.stringify([{ action: 'delete', items: ids }]);

		this.sapiServiceSubject.next({ request: 'delete', entityName, data: ids });

		return this.http.post(url, args, this.determineRequestOptions())
			.pipe(
				share(),
				catchError((err: any) => observableThrowError(err.error as SapiError))
			);
	}

	unlink(entityName: string, id: string): Observable<any> {
		// BACKEND?
		const url = this.locationService.server + '/sapi/' + entityName + '/' + id + '/unlink';

		return this.http.post(url, this.determineRequestOptions());
	}

	getFacetOptions(entityName: string, facetName: string, limit: number = 15, facetPrefix?: string, filterSet?: FilterSet): Observable<SapiFacetResponse> {
		const queryParams: QueryParameter[] = [];
		queryParams.push({ name: 'facet', values: ['on'] } as QueryParameter);
		queryParams.push({ name: 'limit', values: ['0'] } as QueryParameter);
		queryParams.push({ name: 'facetLimit', values: ['50'] } as QueryParameter);
		queryParams.push({ name: 'facetfield[]', values: [facetName] } as QueryParameter);
		// queryParams['facetfield[]'] = [this.facet];
		if (facetPrefix) {
			queryParams.push({ name: 'facetprefix', values: [facetPrefix] } as QueryParameter);
		}
		if (entityName === 'stats') {
			queryParams.push({ name: 'q', values: ['*:*'] } as QueryParameter);
		}
		return this.query(entityName, queryParams, 0, 0, null, null, filterSet).pipe(
			map((response: SapiQueryResponse) => {
				if (response && response.facets && response.facets[facetName] && response.facets[facetName].length) {
					const res: SapiFacetResponse = {
						numfound: response.facets[facetName].length / 2
					};
					const facets = {};
					const facetList = [];
					for (let i = 0; i < response.facets[facetName].length; i = i + 2) {
						const facetKey = response.facets[facetName][i];
						const facetCount = response.facets[facetName][i + 1];
						if (facetKey) {
							facets[facetKey] = facetCount;
							facetList.push(facetKey);
						}
					}

					res.facets = facets;
					res.facetList = facetList;
					return res;
				}
			})
		);

		/*
		if (this.facet && response && response.facets) {
				this.numFound = response.facets / 2;
				for ( let i: number = 0; i < response.facets.length; i = i + 2 ) {
					if (response.facets[i]) {
						this.filteredOptions.push({
							label: response.facets[i],
							value: response.facets[i]
						});
					}
				}
			}
		*/
	}

	query(
		entity: string,
		queryParams: QueryParameter[] = [],
		offset: number = 0,
		limit: number = 15,
		sortProperty: string = 'id',
		sortDirection: string = 'desc',
		filterSet: FilterSet = null
	): Observable<SapiQueryResponse> {

		let params: HttpParams = new HttpParams();
		if (!sortProperty) {
			sortProperty = 'id';
		}
		if (!sortDirection) {
			sortDirection = 'desc';
		}

		for (const queryParam of queryParams) {

			for (const value of queryParam.values) {

				if (queryParam.name === 'q') {

					const qValuesFixed: string[] = [];
					const qValues = value.split(' AND ');

					qValues.forEach(qValue => {

						const valueParts = qValue.split(':');
						if (valueParts[0] === '*') {

							if (valueParts[1] === '*') {
								qValue = '*:*';
							} else if (valueParts[1].match(/^\"([^\"]*)\"$/)) {
								// between quotes? treat as exact match (only)
								qValue = valueParts[1];
							} else {
								// *:test is translated to q=(("test")^200 OR (test OR test*))
								qValue = '(("' + valueParts[1] + '")^200 OR (' + valueParts[1] + ' OR ' + valueParts[1] + '*))';
							}
						}

						qValuesFixed.push(qValue);
					});

					params = params.set('q', qValuesFixed.join(' AND '));
				} else {
					params = params.set(queryParam.name, value);
				}
			}
		}

		params = params.set('limit', limit.toString());
		params = params.set('offset', offset.toString());
		if (!params.get('sort')) {
			params = params.set('sort', sortProperty + ' ' + sortDirection);
		}

		if (filterSet) {
			params = params.set('filterset', JSON.stringify(filterSet));
		}

		const requestOptions = this.determineRequestOptions();
		requestOptions.params = params;

		const url = this.locationService.server + '/sapi/' + entity;

		return this.http.get(url, requestOptions)
			.pipe(
				map((res: any) => {
					if (!res) {
						res = {};
					}
					if (res && res.items && res.items.length) {
						this.sapiServiceSubject.next({ request: 'query', entityName: entity, data: res.items });
					}
					return {
						totalAmount: res.numfound || 0,
						items: res.items || [],
						facets: res.facets || {},
						facetList: res.facetList || [],
						query: res.query || ''
					};
				}),
				catchError((error: Error) => observableThrowError(error || 'Server error'))
			);
	}
}
